import React, { useState } from 'react';
import Image from 'next/legacy/image';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import isEmpty from 'lodash/isEmpty';

import { IWebBannerFields } from '../../../types/contentful';

import { usePublicRuntimeConfig } from '../../hooks/usePublicRuntimeConfig';

import cx from 'classnames';
import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { UrlByEnv, envEnum } from '../../context/PageData';

import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';
import { Flexbox } from '../Flexbox';
import { Text } from '../Text';
import Link from '../Link';

import { InlineAnchor } from './styles';

import toggleCloseBento from '@/images/web-banner-close-icon.svg';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { useSpring, animated } from '@react-spring/web';

export interface WebBannerProps extends IWebBannerFields {
  toggleBanner: Function;
  roundedCornersVariation?: boolean;
}

export function WebBanner({
  title,
  titleColor,
  bannerBackgroundColor,
  message,
  messageColor,
  linkColor,
  mobileScroll,
  rightButtonText, //Called CTA Button Text in Contentful
  ctaButtonColor,
  ctaUrl,
  previewCtaUrl,
  stagingCtaUrl,
  openInNewTab,
  toggleBanner,
  roundedCornersVariation
}: WebBannerProps) {
  const { nodeEnv } = usePublicRuntimeConfig();
  const [ctaScrollPosition, setCtaScrollPosition] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [toggleBannerState, setToggleBannerState] = useState(false);

  useScrollPosition(
    ({ currPos }) => {
      if (currPos.y > 400) setCtaScrollPosition(false);
      else if (currPos.y <= 352) setCtaScrollPosition(true);
    },
    [ctaScrollPosition],
    undefined,
    true,
    100
  );

  const handleCTAClick = () => {
    // Fire Segment Event
    trackWithFingerprint('CTA Clicked', {
      page_url: window.location.href,
      module: 'web banner'
    });
  };

  const target = openInNewTab ? '_blank' : '_self';

  const env = (nodeEnv as keyof typeof envEnum) || '';

  const envCtaUrls: UrlByEnv = {
    development: previewCtaUrl as string,
    preview: previewCtaUrl as string,
    staging: stagingCtaUrl as string,
    production: ctaUrl as string
  };

  // Use CTA url based on env, if undefined don't show CTA button
  const url = envCtaUrls[envEnum[env]];

  const animationStyles = useSpring({
    opacity: toggleBannerState ? 0 : 1,
    transform: toggleBannerState ? 'translateY(-100px)' : 'translateY(0px)',
    config: {
      tension: 300,
      friction: 26,
      mass: 1,
      duration: 462
    },
    onRest: () => {
      if (toggleBannerState) {
        toggleBanner('hide');
      }
    }
  });

  return isEmpty(message) ? null : (
    <animated.div
      data-testid="webBanner"
      style={animationStyles}
      className={cx(
        'web-banner m-auto w-full overflow-hidden bg-white-1000 transition-all duration-300 ease-in-out desktop:visible desktop:relative desktop:max-h-full desktop:translate-y-0 desktop:opacity-100',
        {
          'max-w-[1500px]': roundedCornersVariation,
          'max-w-auto': !roundedCornersVariation,
          // Mobile visible state
          'tiny:visible tiny:max-h-[64px] tiny:translate-y-0 tiny:opacity-100':
            mobileScroll && ctaScrollPosition,
          // Mobile hidden state
          'tiny:invisible tiny:max-h-0 tiny:translate-y-full tiny:opacity-0':
            mobileScroll && !ctaScrollPosition
        }
      )}
    >
      <div
        className="m-auto flex h-[64px] w-screen min-[651px]:h-[56px]"
        style={{
          backgroundColor: bannerBackgroundColor ?? '#1D252C'
        }}
      >
        <Flexbox
          justifyContent="center"
          alignItems="center"
          marginLeft={{ 0: 's' }}
          marginRight={{ 0: 's' }}
        ></Flexbox>
        <div className="flex flex-1 items-center tiny:my-2 tiny:justify-start desktop:my-3 desktop:justify-center">
          <RichText
            data={message}
            overrideOptions={{
              [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                <Text
                  as="p"
                  fontWeight="p"
                  fontSize={{ 0: 's', tablet: 's', desktop: 'sm' }}
                  lineHeight={{ 0: 's', desktop: 'smm' }}
                  className="web-banner-text"
                  textAlign="left"
                  style={{ color: messageColor ?? '#1D252C' }}
                  marginRight={{ 0: 0 }}
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    fontSize={{ 0: 's', tablet: 's', desktop: 'sm' }}
                    textAlign="left"
                    style={{ color: titleColor ?? '#1D252C' }}
                  >
                    {title}
                  </Text>
                  {children}
                </Text>
              ),
              [INLINES.HYPERLINK]: (node, children) => (
                <InlineAnchor
                  href={node.data.uri}
                  style={{
                    color: linkColor ? linkColor : '#111212',
                    marginLeft: '16px'
                  }}
                >
                  {children}
                </InlineAnchor>
              ),
              [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                <EmbeddedInlineEntryNode
                  node={node}
                  colour="#F3F6F5"
                  fontWeight="p"
                >
                  {children}
                </EmbeddedInlineEntryNode>
              )
            }}
          />
          {url && (
            <Flexbox marginLeft={{ 0: 'm' }}>
              <Link
                href={url}
                target={target}
                rel="noreferrer"
                aria-label={rightButtonText ?? ''}
                data-testid="webBannerCtaLink"
                attachParams={true}
                onClick={handleCTAClick}
                style={{
                  backgroundColor: isHovered
                    ? '#0a7266'
                    : ctaButtonColor ?? '#003835'
                }}
                className="my-auto flex h-10 items-center justify-center gap-2 rounded-[8px] px-3 py-4 text-white-1000 tiny:min-w-[110px] desktop:w-[148px]"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <Text
                  as="p"
                  fontWeight="p"
                  fontSize={{ 0: 's', desktop: 's' }}
                  textAlign="center"
                  colour="inherit"
                >
                  {rightButtonText}
                </Text>
              </Link>
            </Flexbox>
          )}
        </div>
        <div
          onClick={() => setToggleBannerState(true)}
          data-testid="webBannerCloseButton"
          className="mx-4 flex h-[24px] w-[24px] cursor-pointer items-center justify-center self-center rounded-[8px]"
          style={{ lineHeight: 0 }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              setToggleBannerState(true);
            }
          }}
        >
          <Image src={toggleCloseBento} alt="close banner" />
        </div>
      </div>
    </animated.div>
  );
}
